import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Wand2, Trash2, ChevronRight } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Page, Main, Card, Button, Thumb, LoadingState, ErrorState, EmptyState, Spinner } from '@/components/ui';
import { useApi } from '@/hooks/useApi';
import api from '@/lib/api';
import { formatRelativeDate } from '@/lib/utils';

interface SessionSummary {
  id: string;
  name: string | null;
  createdAt: string;
  updatedAt: string;
  pieceCount: number;
  thumbnailUrl: string | null;
  context: string;
}

export function FitBuilderSessionsPage() {
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useApi<{ sessions: SessionSummary[] }>('/fit-sessions');
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const sessions = data?.sessions ?? [];

  const startNew = async () => {
    setCreating(true);
    try {
      const res = await api.post<{ session: { id: string } }>('/fit-sessions', { state: {} });
      if (res?.session?.id) navigate(`/fits/builder?session=${res.session.id}`);
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id: string) => {
    setDeletingId(id);
    try {
      await api.delete(`/fit-sessions/${id}`);
      await refetch();
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Page>
      <PageHeader section="Fit Builder" backHref="/fits" backLabel="Fits" />
      <Main className="space-y-4">
        <Card className="p-4 flex items-start gap-3 bg-primary/5 border-primary/20">
          <Wand2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            Build outfits by hand or with the AI stylist. In-progress builds are saved here automatically — pick up
            any of them where you left off, or start a new one.
          </p>
        </Card>

        <Button fullWidth onClick={startNew} disabled={creating}>
          {creating ? <Spinner className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          New Session
        </Button>

        {loading && <LoadingState label="Loading sessions…" />}
        {error && <ErrorState message={error} />}

        {!loading && !error && sessions.length === 0 && (
          <EmptyState
            icon={<Wand2 className="w-8 h-8" />}
            title="No sessions yet"
            description="Start a new session to build an outfit. It'll be saved here so you can return anytime."
          />
        )}

        {sessions.length > 0 && (
          <div className="space-y-3">
            {sessions.map((s) => (
              <Card key={s.id} className="overflow-hidden">
                <div className="flex items-center gap-3 p-3">
                  <button
                    onClick={() => navigate(`/fits/builder?session=${s.id}`)}
                    className="flex items-center gap-3 flex-1 min-w-0 text-left"
                  >
                    <Thumb url={s.thumbnailUrl} alt="" className="w-14 h-14 rounded-xl flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-foreground truncate">
                        {s.name?.trim() || 'Untitled session'}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {s.pieceCount} {s.pieceCount === 1 ? 'piece' : 'pieces'} · edited {formatRelativeDate(s.updatedAt)}
                      </p>
                      {s.context && <p className="text-xs text-muted-foreground/80 truncate mt-0.5">{s.context}</p>}
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  </button>
                  <button
                    onClick={() => remove(s.id)}
                    disabled={deletingId === s.id}
                    className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/5 transition-colors flex-shrink-0"
                    aria-label="Delete session"
                  >
                    {deletingId === s.id ? <Spinner className="w-4 h-4" /> : <Trash2 className="w-4 h-4" />}
                  </button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </Main>
    </Page>
  );
}
